import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Table from "../../../common/Table";
import Loader from "../../../common/Loader";
import Pagination from "../../../common/Pagination";
import SaveBtn from "../../../common/SaveBtn";
import dataService from "../../../../helpers/dataService";
import tools from "../../../../helpers/tools";

// Délai avant de rafraîchir après un rejeu : le traitement se fait dans le
// worker, on laisse le temps à la file d'être consommée avant de recharger.
const DELAI_RAFRAICHISSEMENT_MS = 5000;

/**
 * Tableau des lignes en erreur d'un import, avec les actions de rejeu
 * (globale et ligne par ligne). Ce composant ne s'affiche, côté appelant,
 * que si `compteurs.enErreur > 0`.
 */
const LignesEnErreurTable = ({ importId, onApresRejeu }) => {
  const [lignes, setLignes] = useState([]);
  const [page, setPage] = useState(1);
  const [nbItemsByPage, setNbItemsByPage] = useState(50);
  const [nbPages, setNbPages] = useState(1);
  const [nbResults, setNbResults] = useState(0);
  const [nbNonResolues, setNbNonResolues] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState(false);
  const [rejeuEnCoursId, setRejeuEnCoursId] = useState(null);
  const [rejeuToutEnCours, setRejeuToutEnCours] = useState(false);

  useEffect(() => {
    chargerLignes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId, page, nbItemsByPage]);

  function chargerLignes() {
    setIsLoading(true);
    setErrors(false);
    const parPage = parseInt(nbItemsByPage);
    dataService.get(
      `imports/${importId}/lignes-erreur?page=${page}&parPage=${parPage}`,
      (data) => {
        setLignes(data.lignes);
        setNbResults(data.total);
        setNbNonResolues(data.totalNonResolues ?? data.total);
        setNbPages(Math.max(1, Math.ceil(data.total / parPage)));
      },
      setErrors,
      () => setIsLoading(false)
    );
  }

  function changerNbItemsByPage(valeur) {
    setNbItemsByPage(valeur);
    setPage(1);
  }

  function rejouer(ligneIds, apresEnvoi) {
    // L'API ne renvoie plus de compteur de lignes mises en file : le rejeu est
    // asynchrone, seul le worker sait combien de lignes sont réellement
    // concernées (et le rejeu global envoie un tableau vide, donc un
    // compteur basé sur `ligneIds.length` afficherait toujours 0). On se
    // contente d'un message sans chiffre, adapté selon qu'on connaît déjà
    // (côté front) le nombre de lignes demandées.
    const messageConfirmation =
      ligneIds.length === 1
        ? "Ligne mise en file pour rejeu"
        : "Rejeu mis en file";

    dataService.post(
      `imports/${importId}/rejouer`,
      { ligneIds },
      () => {
        toast.success(messageConfirmation);
        setTimeout(() => {
          chargerLignes();
          if (onApresRejeu) {
            onApresRejeu();
          }
        }, DELAI_RAFRAICHISSEMENT_MS);
      },
      // L'erreur est déjà remontée en toast par dataService : on ne remplace
      // pas le tableau de lignes par un message d'erreur pour un simple échec
      // de rejeu.
      () => {},
      apresEnvoi
    );
  }

  function rejouerLigne(ligneId) {
    setRejeuEnCoursId(ligneId);
    rejouer([ligneId], () => setRejeuEnCoursId(null));
  }

  function rejouerTout() {
    setRejeuToutEnCours(true);
    rejouer([], () => setRejeuToutEnCours(false));
  }

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 style={{ fontSize: "1.1rem" }} className="mb-0">
          Lignes en erreur :{" "}
          {nbNonResolues === 0 ? (
            <span className="text-success">
              toutes réparées ({nbResults} au total)
            </span>
          ) : (
            <>
              {nbNonResolues} non résolue{nbNonResolues > 1 ? "s" : ""} sur{" "}
              {nbResults}
            </>
          )}
        </h2>
        <SaveBtn
          type="btn-warning"
          text="Rejouer toutes les lignes en erreur"
          save={rejouerTout}
          isSaving={rejeuToutEnCours}
          // Sans cette garde, le bouton resterait cliquable une fois tout réparé
          // et annoncerait un rejeu qui ne porterait sur aucune ligne.
          disabled={rejeuEnCoursId !== null || nbNonResolues === 0}
          margin="mt-0"
        />
      </div>

      {errors && errors.other ? (
        <div className="text-danger m-auto text-center">{errors.other}</div>
      ) : isLoading ? (
        <Loader />
      ) : (
        <>
          <Table
            bgTransparent
            datas={lignes}
            rowStyle={(ligne) =>
              ligne.statutRejeu === "REJOUEE_OK"
                ? { backgroundColor: "#d4edda" }
                : {}
            }
            fields={[
              { name: "N° ligne", path: "numeroLigne" },
              {
                name: "Identifiant métier",
                path: "identifiantMetier",
                render: (el) => el || "-",
              },
              { name: "Erreur à l'import", path: "erreur" },
              {
                name: "Erreur au dernier rejeu",
                path: "erreurRejeu",
                render: (el) => el || "-",
              },
              {
                name: "Statut de rejeu",
                path: "*",
                render: (el) =>
                  el.statutRejeu === "REJOUEE_OK" ? (
                    <span className="text-success font-weight-bold">
                      <i className="fa fa-check-circle mr-1" />
                      {el.statutRejeuLabel}
                    </span>
                  ) : (
                    el.statutRejeuLabel
                  ),
              },
              {
                name: "Rejouée le",
                path: "rejoueeLe",
                render: (el) => (el ? tools.formatDate(el, "dd/MM/yyyy HH:mm:ss") : "-"),
              },
              {
                name: "Action",
                path: "*",
                render: (elem) => (
                  <SaveBtn
                    type="btn-outline-warning"
                    text="Rejouer"
                    save={() => rejouerLigne(elem.id)}
                    isSaving={rejeuEnCoursId === elem.id}
                    disabled={
                      elem.statutRejeu === "REJOUEE_OK" ||
                      rejeuToutEnCours ||
                      (rejeuEnCoursId !== null && rejeuEnCoursId !== elem.id)
                    }
                    margin="mt-0"
                  />
                ),
              },
            ]}
          />
          <Pagination
            nbPages={nbPages}
            page={page}
            changePage={setPage}
            nbItemsByPage={nbItemsByPage}
            changeNbItemsByPage={changerNbItemsByPage}
          />
        </>
      )}
    </div>
  );
};

export default LignesEnErreurTable;
